import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import DateTimeDisplay from './DateTimeDisplay';
import VerifiedBankAccountFields from './VerifiedBankAccountFields';

const UnifiedSalaryManagement = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedEmployees, setSelectedEmployees] = useState(new Set());
  const [paymentType, setPaymentType] = useState('individual');
  const [payoutMonth, setPayoutMonth] = useState(new Date().toISOString().slice(0, 7));
  const [customTransferForm, setCustomTransferForm] = useState({
    recipientName: '',
    amount: '',
    reason: '',
    bankAccount: {
      accountName: '',
      bankName: '',
      bankCode: '',
      bankId: '',
      accountNumber: '',
      verified: false,
      verifiedAt: ''
    }
  });
  const [fixedTransferForm, setFixedTransferForm] = useState({
    amount: '',
    reason: ''
  });
  const [showAddSalaryModal, setShowAddSalaryModal] = useState(false);
  const [expandedCards, setExpandedCards] = useState(new Set());

  // Filters
  const [filters, setFilters] = useState({
    employeeType: 'all',
    department: 'all',
    search: '',
    paymentStatus: 'all',
    month: ''
  });

  // Add salary form state
  const [salaryForm, setSalaryForm] = useState({
    employeeId: '',
    employeeType: 'teacher',
    month: new Date().toISOString().slice(0, 7),
    baseSalary: '',
    allowances: {
      housing: '',
      transport: '',
      medical: ''
    },
    deductions: {
      tax: '',
      insurance: ''
    },
    paymentStatus: 'pending',
    paymentMethod: 'bank_transfer',
    notes: ''
  });

  useEffect(() => {
    // Check if admin is logged in
    const adminUser = localStorage.getItem('adminUser');
    if (!adminUser) {
      navigate('/admin');
      return;
    }

    loadEmployees();
    loadStats();
    loadPaymentHistory();
  }, [navigate]);

  const loadEmployees = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.SALARIES}/employees`);
      if (response.ok) {
        const data = await response.json();
        setEmployees(data);
      }
    } catch (error) {
      console.error('Error loading employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSalaries = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.SALARIES}/all`);
      if (response.ok) {
        const data = await response.json();
        setSalaries(data);
      }
    } catch (error) {
      console.error('Error loading salaries:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.SALARIES}/stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadPaymentHistory = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.SALARIES}/payments`);
      if (response.ok) {
        const data = await response.json();
        setPaymentHistory(data);
      }
    } catch (error) {
      console.error('Error loading payment history:', error);
    }
  };

  const handleEmployeeSelection = (employeeId) => {
    const newSelection = new Set(selectedEmployees);
    if (paymentType === 'individual') {
      // For individual payments, only allow one selection
      newSelection.clear();
      newSelection.add(employeeId);
    } else {
      // For batch payments, toggle selection
      if (newSelection.has(employeeId)) {
        newSelection.delete(employeeId);
      } else {
        newSelection.add(employeeId);
      }
    }
    setSelectedEmployees(newSelection);
  };

  const handleInitiatePayment = async () => {
    if (selectedEmployees.size === 0) {
      alert('Please select at least one employee for payment.');
      return;
    }

    const employeeIds = Array.from(selectedEmployees);

    try {
      const response = await fetch(`${API_ENDPOINTS.SALARIES}/initiate-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          employeeIds,
          paymentType,
          salaryMonth: payoutMonth,
          redirectUrl: window.location.origin + '/salary-payment-success'
        })
      });

      const result = await response.json().catch(() => ({}));

      if (response.ok) {
        if (result.payment?.transferResults) {
          const transfers = result.payment.transferResults;
          const failed = transfers.filter((transfer) => transfer.status === 'failed');
          const accepted = transfers.length - failed.length;
          alert(
            failed.length > 0
              ? `Salary payout started for ${accepted} employee(s). ${failed.length} transfer(s) failed.`
              : `Salary payout initiated successfully for ${accepted} employee(s).`
          );
          setSelectedEmployees(new Set());
          loadStats();
          loadPaymentHistory();
        } else if (result.payment && result.payment.link) {
          window.location.href = result.payment.link;
        } else {
          alert(result.message || 'Payment response received, but no transfer details were returned.');
        }
      } else {
        alert(result.details || result.error || 'Failed to initiate payment');
      }
    } catch (error) {
      console.error('Error initiating payment:', error);
      alert('Failed to initiate payment');
    }
  };

  const resetCustomTransferForm = () => {
    setCustomTransferForm({
      recipientName: '',
      amount: '',
      reason: '',
      bankAccount: {
        accountName: '',
        bankName: '',
        bankCode: '',
        bankId: '',
        accountNumber: '',
        verified: false,
        verifiedAt: ''
      }
    });
  };

  const handleCustomTransfer = async () => {
    if (!customTransferForm.recipientName.trim()) {
      alert('Enter recipient name.');
      return;
    }
    if (!customTransferForm.reason.trim()) {
      alert('Enter payment reason.');
      return;
    }
    if (!customTransferForm.bankAccount?.verified) {
      alert('Verify the recipient bank account before sending payment.');
      return;
    }

    const amount = Number(customTransferForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      alert('Enter a valid transfer amount.');
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.SALARIES}/custom-transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'custom',
          amount,
          reason: customTransferForm.reason,
          recipient: {
            name: customTransferForm.recipientName,
            bankAccount: customTransferForm.bankAccount
          }
        })
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        alert(result.details || result.error || 'Failed to initiate custom transfer');
        return;
      }

      const failed = result.payment?.failedTransfers?.length || 0;
      alert(failed > 0 ? `Custom transfer started with ${failed} failed transfer(s).` : 'Custom transfer initiated successfully.');
      resetCustomTransferForm();
      loadPaymentHistory();
    } catch (error) {
      console.error('Error initiating custom transfer:', error);
      alert('Failed to initiate custom transfer');
    }
  };

  const handleFixedAmountTransfer = async () => {
    if (selectedEmployees.size === 0) {
      alert('Select at least one employee for fixed amount transfer.');
      return;
    }
    if (!fixedTransferForm.reason.trim()) {
      alert('Enter payment reason.');
      return;
    }

    const amount = Number(fixedTransferForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      alert('Enter a valid fixed transfer amount.');
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.SALARIES}/custom-transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'staff-fixed',
          employeeIds: Array.from(selectedEmployees),
          amount,
          reason: fixedTransferForm.reason
        })
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        alert(result.details || result.error || 'Failed to initiate fixed amount transfer');
        return;
      }

      const failed = result.payment?.failedTransfers?.length || 0;
      alert(failed > 0 ? `Fixed amount transfer started with ${failed} failed transfer(s).` : 'Fixed amount transfer initiated successfully.');
      setFixedTransferForm({ amount: '', reason: '' });
      setSelectedEmployees(new Set());
      loadPaymentHistory();
    } catch (error) {
      console.error('Error initiating fixed amount transfer:', error);
      alert('Failed to initiate fixed amount transfer');
    }
  };

  const getFilteredEmployees = () => {
    return employees.filter(employee => {
      if (filters.employeeType !== 'all' && employee.employeeType !== filters.employeeType) return false;
      if (filters.department !== 'all' && employee.department !== filters.department) return false;
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        return employee.name.toLowerCase().includes(searchLower) ||
               employee.employeeId.toLowerCase().includes(searchLower) ||
               employee.email.toLowerCase().includes(searchLower);
      }
      return true;
    });
  };

  const getSelectedEmployeeDetails = () => {
    return employees.filter(emp => selectedEmployees.has(emp.employeeId));
  };

  const getTotalPaymentAmount = () => {
    return getSelectedEmployeeDetails().reduce((total, emp) => {
      return total + (emp.netSalary || 0);
    }, 0);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    }).catch(() => {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Copied to clipboard!');
    });
  };

  const resetSalaryForm = () => {
    setSalaryForm({
      employeeId: '',
      employeeType: 'teacher',
      month: new Date().toISOString().slice(0, 7),
      baseSalary: '',
      allowances: { housing: '', transport: '', medical: '' },
      deductions: { tax: '', insurance: '' },
      paymentStatus: 'pending',
      paymentMethod: 'bank_transfer',
      notes: ''
    });
  };

  const toggleCardExpansion = (salaryId) => {
    const newExpandedCards = new Set(expandedCards);
    if (newExpandedCards.has(salaryId)) {
      newExpandedCards.delete(salaryId);
    } else {
      newExpandedCards.add(salaryId);
    }
    setExpandedCards(newExpandedCards);
  };

  const formatMonthLabel = (month) => {
    if (!month) return 'Unknown month';
    const date = new Date(`${month}-01T00:00:00`);
    return Number.isNaN(date.getTime())
      ? month
      : date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  };

  const getPaymentMonth = (payment) => (
    payment.salaryMonth ||
    payment.transferResults?.[0]?.salaryMonth ||
    (payment.initiatedAt ? payment.initiatedAt.slice(0, 7) : '')
  );

  const getTransferDisplayStatus = (transfer) => {
    const status = (transfer?.status || '').toString().toLowerCase();
    if (!transfer) return 'Not Paid';
    if (status === 'failed') return 'Failed';
    if (['successful', 'success', 'completed'].includes(status)) return 'Paid';
    return 'Paid';
  };

  const getEmployeeMonthPayment = (employeeId, month) => {
    const monthPayments = paymentHistory
      .filter((payment) => ['individual', 'batch'].includes(payment.paymentType) && getPaymentMonth(payment) === month)
      .sort((a, b) => new Date(b.initiatedAt || 0) - new Date(a.initiatedAt || 0));

    for (const payment of monthPayments) {
      const transfer = (payment.transferResults || []).find((item) => item.employeeId === employeeId);
      if (transfer) {
        return {
          payment,
          transfer,
          status: getTransferDisplayStatus(transfer)
        };
      }
    }

    return {
      payment: null,
      transfer: null,
      status: 'Not Paid'
    };
  };

  const filteredEmployees = getFilteredEmployees();
  const selectedEmployeesDetails = getSelectedEmployeeDetails();
  const totalAmount = getTotalPaymentAmount();
  const monthlyPaymentRows = employees
    .map((employee) => ({
      ...employee,
      payment: getEmployeeMonthPayment(employee.employeeId, payoutMonth)
    }))
    .sort((a, b) => {
      const order = { Failed: 0, 'Not Paid': 1, Paid: 2 };
      return (order[a.payment.status] ?? 3) - (order[b.payment.status] ?? 3) || a.name.localeCompare(b.name);
    });
  const monthlyPaymentSummary = monthlyPaymentRows.reduce((summary, employee) => {
    if (employee.payment.status === 'Paid') summary.paid += 1;
    if (employee.payment.status === 'Failed') summary.failed += 1;
    if (employee.payment.status === 'Not Paid') summary.unpaid += 1;
    return summary;
  }, { paid: 0, failed: 0, unpaid: 0 });
  const historyTotals = paymentHistory.reduce((summary, payment) => {
    const transfers = payment.transferResults || [];
    summary.amount += Number(payment.totalAmount) || 0;
    summary.batches += 1;
    summary.transfers += transfers.length;
    summary.failed += transfers.filter((transfer) => transfer.status === 'failed').length;
    return summary;
  }, { amount: 0, batches: 0, transfers: 0, failed: 0 });

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-blue-900">Salary Payment System</h1>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition duration-300"
            >
              Back to Dashboard
            </button>
            <DateTimeDisplay />
          </div>
        </div>

        {/* Payment Type Selector */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200 mb-6">
          <h2 className="text-xl font-semibold text-blue-800 mb-4">Payment Type</h2>
          <div className="flex space-x-6">
            <label className="flex items-center">
              <input
                type="radio"
                value="individual"
                checked={paymentType === 'individual'}
                onChange={(e) => {
                  setPaymentType(e.target.value);
                  setSelectedEmployees(new Set());
                }}
                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="font-medium text-gray-900">Individual Payment</span>
                <p className="text-sm text-gray-500">Pay one employee at a time</p>
              </div>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="batch"
                checked={paymentType === 'batch'}
                onChange={(e) => {
                  setPaymentType(e.target.value);
                  setSelectedEmployees(new Set());
                }}
                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="font-medium text-gray-900">Batch Payment</span>
                <p className="text-sm text-gray-500">Pay multiple employees together</p>
              </div>
            </label>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 bg-white border border-slate-200 rounded-xl shadow-sm p-2 mb-6">
          <button
            onClick={() => {
              setActiveTab('overview');
              loadStats();
            }}
            className={`px-6 py-3 font-bold text-sm rounded-lg transition-colors ${
              activeTab === 'overview'
                ? 'bg-slate-950 text-white shadow'
                : 'text-slate-600 bg-slate-50 hover:bg-emerald-50 hover:text-emerald-800'
            }`}
          >
            Stats Overview
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            className={`px-6 py-3 font-bold text-sm rounded-lg transition-colors ${
              activeTab === 'payments'
                ? 'bg-slate-950 text-white shadow'
                : 'text-slate-600 bg-slate-50 hover:bg-emerald-50 hover:text-emerald-800'
            }`}
          >
            💰 Payment Processing
          </button>
          <button
            onClick={() => {
              setActiveTab('records');
              loadSalaries();
              loadStats();
            }}
            className={`px-6 py-3 font-bold text-sm rounded-lg transition-colors ${
              activeTab === 'records'
                ? 'bg-slate-950 text-white shadow'
                : 'text-slate-600 bg-slate-50 hover:bg-emerald-50 hover:text-emerald-800'
            }`}
          >
            Applications Salary Records ({salaries.length})
          </button>
          <button
            onClick={() => {
              setActiveTab('history');
              loadPaymentHistory();
            }}
            className={`px-6 py-3 font-bold text-sm rounded-lg transition-colors ${
              activeTab === 'history'
                ? 'bg-slate-950 text-white shadow'
                : 'text-slate-600 bg-slate-50 hover:bg-emerald-50 hover:text-emerald-800'
            }`}
          >
            📜 Payment History
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Total Salary Summary - Always Visible */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-lg p-8 text-white">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-3xl font-bold mb-2">Total Salary Liability</h2>
                  <p className="text-blue-100">All active teachers and staff combined</p>
                </div>
                <div className="text-right">
                  <div className="text-4xl font-bold mb-1">
                    ₦{((stats.totalAllSalaries || 0)).toLocaleString()}
                  </div>
                  <div className="text-sm text-blue-100">Monthly Total</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white bg-opacity-20 rounded-lg p-4">
                  <div className="text-2xl font-bold mb-1">
                    ₦{((stats.totalTeacherSalaries || 0)).toLocaleString()}
                  </div>
                  <div className="text-sm text-blue-100">Teachers ({stats.teachersCount || 0})</div>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg p-4">
                  <div className="text-2xl font-bold mb-1">
                    ₦{((stats.totalStaffSalaries || 0)).toLocaleString()}
                  </div>
                  <div className="text-sm text-blue-100">Staff ({stats.staffCount || 0})</div>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg p-4">
                  <div className="text-lg font-bold mb-1">
                    {stats.totalEmployees || 0} Employees
                  </div>
                  <div className="text-sm text-blue-100">Total Active</div>
                </div>
              </div>
            </div>

            {/* Payment Status Overview */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Payment Summary */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
                <h3 className="text-xl font-semibold text-blue-800 mb-6">Payment Status Summary</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                      <span className="font-medium text-green-800">Paid</span>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-green-600">
                        ₦{((stats.totalAmountPaid || 0)).toLocaleString()}
                      </div>
                      <div className="text-sm text-green-600">{stats.paidSalaries || 0} records</div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center p-4 bg-yellow-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
                      <span className="font-medium text-yellow-800">Pending</span>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-yellow-600">
                        ₦{((stats.totalAmountPending || 0)).toLocaleString()}
                      </div>
                      <div className="text-sm text-yellow-600">{stats.pendingSalaries || 0} records</div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                      <span className="font-medium text-blue-800">Processing</span>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-blue-600">
                        ₦{((stats.totalAmountProcessing || 0)).toLocaleString()}
                      </div>
                      <div className="text-sm text-blue-600">{stats.processingSalaries || 0} records</div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center p-4 bg-red-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                      <span className="font-medium text-red-800">Overdue</span>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-red-600">
                        ₦{((stats.totalAmountOverdue || 0)).toLocaleString()}
                      </div>
                      <div className="text-sm text-red-600">{stats.overdueSalaries || 0} records</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
                <h3 className="text-xl font-semibold text-blue-800 mb-6">Quick Actions</h3>
                <div className="space-y-4">
                  <button
                    onClick={() => setActiveTab('payments')}
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition duration-300 flex items-center justify-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    <span>Process Payments</span>
                  </button>

                  <button
                    onClick={() => setActiveTab('records')}
                    className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition duration-300 flex items-center justify-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Manage Records</span>
                  </button>

                  <button
                    onClick={() => setShowAddSalaryModal(true)}
                    className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 transition duration-300 flex items-center justify-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span>Add Salary Record</span>
                  </button>
                </div>

                {/* Recent Activity */}
                <div className="mt-8">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">Recent Activity</h4>
                  <div className="space-y-3">
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Last updated:</span> {new Date().toLocaleDateString()}
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Total records:</span> {stats.totalSalaries || 0}
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Active employees:</span> {stats.totalEmployees || 0}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Employee List */}
            <div className="lg:col-span-2">
            {/* Filters */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200 mb-6">
              <h2 className="text-xl font-semibold text-blue-800 mb-4">Select Employees</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Salary Month</label>
                  <input
                    type="month"
                    value={payoutMonth}
                    onChange={(e) => setPayoutMonth(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <select
                  value={filters.employeeType}
                  onChange={(e) => setFilters({...filters, employeeType: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Types</option>
                  <option value="teacher">Teachers</option>
                  <option value="staff">Staff</option>
                </select>
                <select
                  value={filters.department}
                  onChange={(e) => setFilters({...filters, department: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Departments</option>
                  <option value="Teaching">Teaching</option>
                  <option value="General">General</option>
                </select>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search by name/ID/email"
                />
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200 mb-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-blue-800">Payment Status for {formatMonthLabel(payoutMonth)}</h2>
                  <p className="text-sm text-gray-600">This marks every active employee as paid, failed, or not paid for the selected month.</p>
                </div>
                <button
                  onClick={loadPaymentHistory}
                  className="px-4 py-2 bg-slate-900 text-white rounded hover:bg-slate-700"
                >
                  Refresh Status
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-700">Paid</p>
                  <p className="text-2xl font-bold text-green-800">{monthlyPaymentSummary.paid}</p>
                </div>
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">Failed</p>
                  <p className="text-2xl font-bold text-red-800">{monthlyPaymentSummary.failed}</p>
                </div>
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-700">Not Paid</p>
                  <p className="text-2xl font-bold text-yellow-800">{monthlyPaymentSummary.unpaid}</p>
                </div>
              </div>

              <div className="overflow-x-auto max-h-96 border border-gray-100 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 sticky top-0">
                    <tr>
                      <th className="text-left px-4 py-3">Employee</th>
                      <th className="text-left px-4 py-3">Type</th>
                      <th className="text-right px-4 py-3">Salary</th>
                      <th className="text-left px-4 py-3">Status</th>
                      <th className="text-left px-4 py-3">Paid Month / Reference</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {monthlyPaymentRows.map((employee) => (
                      <tr key={employee.employeeId}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{employee.name}</p>
                          <p className="text-xs text-gray-500">{employee.employeeId}</p>
                        </td>
                        <td className="px-4 py-3 capitalize">{employee.employeeType}</td>
                        <td className="px-4 py-3 text-right font-semibold">₦{(employee.netSalary || 0).toLocaleString()}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs font-semibold rounded ${
                            employee.payment.status === 'Paid'
                              ? 'bg-green-100 text-green-800'
                              : employee.payment.status === 'Failed'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {employee.payment.status}
                          </span>
                          {employee.payment.transfer?.error && (
                            <p className="text-xs text-red-600 mt-1">{employee.payment.transfer.error}</p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {employee.payment.transfer ? (
                            <>
                              <p className="text-gray-900">{formatMonthLabel(getPaymentMonth(employee.payment.payment))}</p>
                              <p className="font-mono text-xs text-gray-500">{employee.payment.transfer.reference}</p>
                            </>
                          ) : (
                            <span className="text-gray-500">No payment recorded for this month</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Employee Cards */}
            <div className="space-y-4">
              {filteredEmployees.map((employee) => {
                const isSelected = selectedEmployees.has(employee.employeeId);

                return (
                  <div
                    key={employee.employeeId}
                    className={`bg-white rounded-2xl shadow-lg border-2 transition-all duration-200 cursor-pointer ${
                      isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleEmployeeSelection(employee.employeeId)}
                  >
                    <div className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                          }`}>
                            {isSelected && (
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                          <div className={`w-4 h-4 rounded-full ${
                            employee.employeeType === 'teacher' ? 'bg-blue-500' : 'bg-green-500'
                          }`}></div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-800">{employee.name}</h3>
                            <p className="text-sm text-gray-600">
                              {employee.employeeId} ? {employee.employeeType} ? {employee.department}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-green-600">
                            ₦{(employee.netSalary || 0).toLocaleString()}
                          </p>
                          <p className="text-sm text-gray-500">
                            Base: ₦{((employee.baseSalary || 0)).toLocaleString()}
                          </p>
                          {(employee.deductions || 0) > 0 && (
                            <p className="text-sm text-gray-500">
                              Deductions: ₦{((employee.deductions || 0)).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Bank Account Info */}
                      {employee.bankAccount && (employee.bankAccount.accountName || employee.bankAccount.accountNumber) && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="flex items-center justify-between text-sm">
                            <div>
                              <span className="font-medium text-gray-700">Account: </span>
                              <span className="font-mono">{employee.bankAccount.accountName}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-gray-700">Number: </span>
                              <span className="font-mono">{employee.bankAccount.accountNumber}</span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyToClipboard(employee.bankAccount.accountNumber);
                                }}
                                className="text-xs bg-gray-500 text-white px-2 py-1 rounded hover:bg-gray-600"
                              >
                                Copy
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Payment Summary */}
          <div className="space-y-6">
            {/* Selected Employees Summary */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
              <h2 className="text-xl font-semibold text-blue-800 mb-4">
                {paymentType === 'individual' ? 'Selected Employee' : 'Selected Employees'} ({selectedEmployeesDetails.length})
              </h2>
              <p className="text-sm text-gray-600 mb-4">Salary month: <span className="font-semibold">{formatMonthLabel(payoutMonth)}</span></p>

              {selectedEmployeesDetails.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  {paymentType === 'individual' ? 'Select an employee to proceed with payment' : 'Select employees for batch payment'}
                </p>
              ) : (
                <div className="space-y-4">
                  {selectedEmployeesDetails.map((employee) => (
                    <div key={employee.employeeId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <h4 className="font-medium text-gray-800">{employee.name}</h4>
                        <p className="text-sm text-gray-600">{employee.employeeId}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">₦{(employee.netSalary || 0).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}

                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center text-lg font-bold">
                      <span>Total Amount:</span>
                      <span className="text-green-600">₦{totalAmount.toLocaleString()}</span>
                    </div>
                  </div>

                  <button
                    onClick={handleInitiatePayment}
                    className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition duration-300 font-medium flex items-center justify-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    <span>Pay Salaries via Flutterwave</span>
                  </button>

                  <p className="text-xs text-gray-500 text-center">
                    Flutterwave will process bank transfers to the selected employee accounts
                  </p>
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
              <h2 className="text-xl font-semibold text-blue-800 mb-4">Fixed Amount to Selected Employees</h2>
              <p className="text-sm text-gray-600 mb-4">Send the same amount to every selected teacher or staff member without marking it as monthly salary.</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fixed Amount</label>
                  <input
                    type="number"
                    min="1"
                    value={fixedTransferForm.amount}
                    onChange={(e) => setFixedTransferForm({...fixedTransferForm, amount: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Amount per selected employee"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Reason</label>
                  <input
                    type="text"
                    value={fixedTransferForm.reason}
                    onChange={(e) => setFixedTransferForm({...fixedTransferForm, reason: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. Transport allowance, bonus, refund"
                  />
                </div>
                <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-700">
                  <div className="flex justify-between">
                    <span>Selected employees</span>
                    <span className="font-semibold">{selectedEmployeesDetails.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total transfer</span>
                    <span className="font-semibold">
                      ₦{((Number(fixedTransferForm.amount) || 0) * selectedEmployeesDetails.length).toLocaleString()}
                    </span>
                  </div>
                </div>
                <button
                  onClick={handleFixedAmountTransfer}
                  className="w-full bg-emerald-600 text-white py-3 px-6 rounded-lg hover:bg-emerald-700 transition duration-300 font-medium"
                >
                  Send Fixed Amount
                </button>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
              <h2 className="text-xl font-semibold text-blue-800 mb-4">Custom Account Transfer</h2>
              <p className="text-sm text-gray-600 mb-4">Add any verified Nigerian bank account and credit it with a payment reason.</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Name</label>
                  <input
                    type="text"
                    value={customTransferForm.recipientName}
                    onChange={(e) => setCustomTransferForm({...customTransferForm, recipientName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Name to identify this recipient"
                  />
                </div>
                <VerifiedBankAccountFields
                  value={customTransferForm.bankAccount}
                  onChange={(bankAccount) => setCustomTransferForm({...customTransferForm, bankAccount})}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                  <input
                    type="number"
                    min="1"
                    value={customTransferForm.amount}
                    onChange={(e) => setCustomTransferForm({...customTransferForm, amount: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Transfer amount"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Reason</label>
                  <textarea
                    value={customTransferForm.reason}
                    onChange={(e) => setCustomTransferForm({...customTransferForm, reason: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Reason shown in payout records"
                  />
                </div>
                <button
                  onClick={handleCustomTransfer}
                  className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg hover:bg-purple-700 transition duration-300 font-medium"
                >
                  Send Custom Transfer
                </button>
              </div>
            </div>

            {/* Payment Instructions */}
            <div className="bg-blue-50 rounded-2xl p-6 border border-blue-200">
              <h3 className="text-lg font-semibold text-blue-800 mb-3">Payment Process</h3>
              <div className="space-y-2 text-sm text-blue-700">
                <p>1. Select employee(s) for payment</p>
                <p>2. Review total amount and details</p>
                <p>3. Click "Pay Salaries via Flutterwave"</p>
                <p>4. Flutterwave processes bank transfers</p>
                <p>5. Review the payout status after submission</p>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-blue-800 mb-4">Quick Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Employees:</span>
                  <span className="font-semibold">{employees.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Teachers:</span>
                  <span className="font-semibold">{employees.filter(e => e.employeeType === 'teacher').length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Staff:</span>
                  <span className="font-semibold">{employees.filter(e => e.employeeType === 'staff').length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Salary Records Tab */}
        {activeTab === 'records' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
                <div className="text-2xl font-bold text-blue-600">{stats.totalSalaries || 0}</div>
                <div className="text-sm text-gray-600">Total Salary Records</div>
              </div>
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
                <div className="text-2xl font-bold text-green-600">{stats.paidSalaries || 0}</div>
                <div className="text-sm text-gray-600">Paid Salaries</div>
              </div>
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
                <div className="text-2xl font-bold text-yellow-600">{stats.pendingSalaries || 0}</div>
                <div className="text-sm text-gray-600">Pending Payments</div>
              </div>
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
                <div className="text-2xl font-bold text-purple-600">
                  ₦{(stats.totalAmountPaid || 0).toLocaleString()}
                </div>
                <div className="text-sm text-gray-600">Total Paid Amount</div>
              </div>
            </div>

            {/* Add Salary Record Button */}
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-semibold text-blue-800">Salary Records Management</h2>
              <button
                onClick={() => setShowAddSalaryModal(true)}
                className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 transition duration-300"
              >
                Add Salary Record
              </button>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
              <h2 className="text-xl font-semibold text-blue-800 mb-4">Filters</h2>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <select
                  value={filters.employeeType}
                  onChange={(e) => setFilters({...filters, employeeType: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Types</option>
                  <option value="teacher">Teachers</option>
                  <option value="staff">Staff</option>
                </select>
                <select
                  value={filters.paymentStatus}
                  onChange={(e) => setFilters({...filters, paymentStatus: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="processing">Processing</option>
                  <option value="overdue">Overdue</option>
                  <option value="issue">Issue</option>
                </select>
                <input
                  type="month"
                  value={filters.month}
                  onChange={(e) => setFilters({...filters, month: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Filter by month"
                />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search by name/ID"
                />
                <button
                  onClick={() => {
                    setFilters({
                      employeeType: 'all',
                      department: 'all',
                      search: '',
                      paymentStatus: 'all',
                      month: ''
                    });
                  }}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  Clear
                </button>
              </div>
            </div>

            {/* Salary Records */}
            <div className="space-y-4">
              {salaries.filter(salary => {
                if (filters.employeeType !== 'all' && salary.employeeType !== filters.employeeType) return false;
                if (filters.paymentStatus !== 'all' && salary.paymentStatus !== filters.paymentStatus) return false;
                if (filters.month && salary.month !== filters.month) return false;
                if (filters.search) {
                  const searchLower = filters.search.toLowerCase();
                  return salary.employeeName.toLowerCase().includes(searchLower) ||
                         salary.employeeId.toLowerCase().includes(searchLower);
                }
                return true;
              }).map((salary) => {
                const isExpanded = expandedCards.has(salary.id);

                return (
                  <div key={salary.id} className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                    {/* Card Header */}
                    <div
                      className="p-6 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                      onClick={() => toggleCardExpansion(salary.id)}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-4">
                          <div className={`w-4 h-4 rounded-full ${
                            salary.employeeType === 'teacher' ? 'bg-blue-500' : 'bg-green-500'
                          }`}></div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-800">{salary.employeeName}</h3>
                            <p className="text-sm text-gray-600">
                              {salary.employeeId} - {salary.month} - {salary.employeeType}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <p className="text-lg font-bold text-green-600">
                              ₦{salary.netSalary?.toLocaleString() || 0}
                            </p>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              salary.paymentStatus === 'paid'
                                ? 'bg-green-100 text-green-800'
                                : salary.paymentStatus === 'pending'
                                ? 'bg-yellow-100 text-yellow-800'
                                : salary.paymentStatus === 'processing'
                                ? 'bg-blue-100 text-blue-800'
                                : salary.paymentStatus === 'overdue'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {salary.paymentStatus}
                            </span>
                          </div>
                          <button
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
                            onClick={(e) => {
                              e.stopPropagation();
                              const newExpandedCards = new Set(expandedCards);
                              if (newExpandedCards.has(salary.id)) {
                                newExpandedCards.delete(salary.id);
                              } else {
                                newExpandedCards.add(salary.id);
                              }
                              setExpandedCards(newExpandedCards);
                            }}
                          >
                            <svg
                              className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Expandable Content */}
                    <div className={`transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                      <div className="px-6 pb-6 border-t border-gray-100">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
                          {/* Salary Details */}
                          <div className="space-y-4">
                            <h4 className="text-md font-semibold text-gray-700">Salary Breakdown</h4>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="p-3 bg-gray-50 rounded">
                                <div className="text-xs text-gray-500">Base Salary</div>
                                <div className="text-lg font-bold text-gray-900">
                                  ₦{salary.baseSalary?.toLocaleString() || 0}
                                </div>
                              </div>
                              <div className="p-3 bg-green-50 rounded">
                                <div className="text-xs text-gray-500">Total Allowances</div>
                                <div className="text-lg font-bold text-green-700">
                                  ₦{salary.totalAllowances?.toLocaleString() || 0}
                                </div>
                              </div>
                              <div className="p-3 bg-red-50 rounded">
                                <div className="text-xs text-gray-500">Total Deductions</div>
                                <div className="text-lg font-bold text-red-700">
                                  ₦{salary.totalDeductions?.toLocaleString() || 0}
                                </div>
                              </div>
                              <div className="p-3 bg-blue-50 rounded">
                                <div className="text-xs text-gray-500">Net Salary</div>
                                <div className="text-lg font-bold text-blue-700">
                                  ₦{salary.netSalary?.toLocaleString() || 0}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="space-y-4">
                            <h4 className="text-md font-semibold text-gray-700">Actions</h4>
                            <div className="flex space-x-2">
                              <select
                                value={salary.paymentStatus}
                                onChange={async (e) => {
                                  try {
                                    const response = await fetch(`${API_ENDPOINTS.SALARIES}/${salary.id}`, {
                                      method: 'PUT',
                                      headers: {
                                        'Content-Type': 'application/json'
                                      },
                                      body: JSON.stringify({ paymentStatus: e.target.value })
                                    });
                                    if (response.ok) {
                                      loadSalaries();
                                      loadStats();
                                    }
                                  } catch (error) {
                                    console.error('Error updating status:', error);
                                  }
                                }}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="pending">Pending</option>
                                <option value="paid">Paid</option>
                                <option value="processing">Processing</option>
                                <option value="overdue">Overdue</option>
                                <option value="issue">Issue</option>
                              </select>
                              <button
                                onClick={async () => {
                                  if (window.confirm('Are you sure you want to delete this salary record?')) {
                                    try {
                                      const response = await fetch(`${API_ENDPOINTS.SALARIES}/${salary.id}`, {
                                        method: 'DELETE'
                                      });
                                      if (response.ok) {
                                        loadSalaries();
                                        loadStats();
                                      }
                                    } catch (error) {
                                      console.error('Error deleting record:', error);
                                    }
                                  }
                                }}
                                className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Payment History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-2xl font-semibold text-blue-800">Payment Transaction History</h2>
                <p className="text-sm text-gray-600">Flutterwave salary payout batches and transfer-level results.</p>
              </div>
              <button
                onClick={loadPaymentHistory}
                className="px-4 py-2 bg-slate-900 text-white rounded hover:bg-slate-700"
              >
                Refresh History
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <p className="text-sm text-gray-500">Batches</p>
                <p className="text-2xl font-bold text-slate-900">{historyTotals.batches}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <p className="text-sm text-gray-500">Transfers</p>
                <p className="text-2xl font-bold text-blue-700">{historyTotals.transfers}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <p className="text-sm text-gray-500">Failed</p>
                <p className="text-2xl font-bold text-red-700">{historyTotals.failed}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <p className="text-sm text-gray-500">Total Amount</p>
                <p className="text-2xl font-bold text-green-700">₦{historyTotals.amount.toLocaleString()}</p>
              </div>
            </div>

            {paymentHistory.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
                <p className="text-gray-600">No Flutterwave salary payouts have been recorded yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {paymentHistory.map((payment) => {
                  const transfers = payment.transferResults || [];
                  const failedCount = transfers.filter((transfer) => transfer.status === 'failed').length;
                  return (
                    <div key={payment.id || payment.tx_ref} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                      <div className="p-5 border-b border-gray-100 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-lg font-bold text-gray-900">{payment.id || payment.tx_ref}</h3>
                            <span className={`px-2 py-1 text-xs font-semibold rounded ${
                              payment.status === 'failed'
                                ? 'bg-red-100 text-red-800'
                                : payment.status === 'partial'
                                ? 'bg-orange-100 text-orange-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {payment.status || 'processing'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">
                            {payment.paymentMethod || 'flutterwave'} - {payment.paymentType || 'individual'} - {formatMonthLabel(getPaymentMonth(payment))} - {payment.initiatedAt ? new Date(payment.initiatedAt).toLocaleString() : 'No date'}
                          </p>
                          {payment.reason && (
                            <p className="text-sm text-slate-700 mt-1">
                              <span className="font-semibold">Reason:</span> {payment.reason}
                            </p>
                          )}
                        </div>
                        <div className="text-left lg:text-right">
                          <p className="text-xl font-bold text-green-700">₦{(Number(payment.totalAmount) || 0).toLocaleString()}</p>
                          <p className="text-sm text-gray-500">{transfers.length || payment.employeeIds?.length || 0} transfer(s), {failedCount} failed</p>
                        </div>
                      </div>

                      {transfers.length > 0 && (
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 text-gray-600">
                              <tr>
                                <th className="text-left px-5 py-3">Recipient</th>
                                <th className="text-left px-5 py-3">Bank</th>
                                <th className="text-left px-5 py-3">Reference</th>
                                <th className="text-left px-5 py-3">Reason</th>
                                <th className="text-right px-5 py-3">Amount</th>
                                <th className="text-left px-5 py-3">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {transfers.map((transfer) => (
                                <tr key={transfer.reference || transfer.employeeId}>
                                  <td className="px-5 py-3">
                                    <p className="font-medium text-gray-900">{transfer.employeeName}</p>
                                    <p className="text-xs text-gray-500">{transfer.employeeId}</p>
                                  </td>
                                  <td className="px-5 py-3">
                                    <p className="text-gray-900">{transfer.bank?.name || 'N/A'}</p>
                                    <p className="text-xs text-gray-500">{transfer.bank?.accountNumber || ''}</p>
                                  </td>
                                  <td className="px-5 py-3">
                                    <p className="font-mono text-xs text-gray-800">{transfer.reference}</p>
                                    {transfer.transferId && <p className="font-mono text-xs text-gray-500">{transfer.transferId}</p>}
                                  </td>
                                  <td className="px-5 py-3 text-gray-700">{transfer.reason || payment.reason || '-'}</td>
                                  <td className="px-5 py-3 text-right font-semibold">₦{(Number(transfer.amount) || 0).toLocaleString()}</td>
                                  <td className="px-5 py-3">
                                    <span className={`px-2 py-1 text-xs font-semibold rounded ${
                                      transfer.status === 'failed'
                                        ? 'bg-red-100 text-red-800'
                                        : transfer.status === 'SUCCESSFUL' || transfer.status === 'successful'
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-blue-100 text-blue-800'
                                    }`}>
                                      {transfer.status}
                                    </span>
                                    {transfer.error && <p className="text-xs text-red-600 mt-1">{transfer.error}</p>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Salary Modal */}
      {showAddSalaryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Add Salary Record</h2>
                <button
                  onClick={() => setShowAddSalaryModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <form onSubmit={async (e) => {
                e.preventDefault();

                try {
                  const salaryData = {
                    month: salaryForm.month,
                    baseSalary: parseFloat(salaryForm.baseSalary) || 0,
                    allowances: {
                      housing: parseFloat(salaryForm.allowances.housing) || 0,
                      transport: parseFloat(salaryForm.allowances.transport) || 0,
                      medical: parseFloat(salaryForm.allowances.medical) || 0
                    },
                    deductions: {
                      tax: parseFloat(salaryForm.deductions.tax) || 0,
                      insurance: parseFloat(salaryForm.deductions.insurance) || 0
                    },
                    totalAllowances: (parseFloat(salaryForm.allowances.housing) || 0) +
                                    (parseFloat(salaryForm.allowances.transport) || 0) +
                                    (parseFloat(salaryForm.allowances.medical) || 0),
                    totalDeductions: (parseFloat(salaryForm.deductions.tax) || 0) +
                                    (parseFloat(salaryForm.deductions.insurance) || 0),
                    netSalary: (parseFloat(salaryForm.baseSalary) || 0) +
                              ((parseFloat(salaryForm.allowances.housing) || 0) +
                               (parseFloat(salaryForm.allowances.transport) || 0) +
                               (parseFloat(salaryForm.allowances.medical) || 0)) -
                              ((parseFloat(salaryForm.deductions.tax) || 0) +
                               (parseFloat(salaryForm.deductions.insurance) || 0)),
                    paymentStatus: salaryForm.paymentStatus,
                    paymentMethod: salaryForm.paymentMethod,
                    notes: salaryForm.notes
                  };

                  const response = await fetch(`${API_ENDPOINTS.SALARIES}/add`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      employeeId: salaryForm.employeeId,
                      employeeType: salaryForm.employeeType,
                      salaryData
                    })
                  });

                  if (response.ok) {
                    alert('Salary record added successfully!');
                    setShowAddSalaryModal(false);
                    resetSalaryForm();
                    loadSalaries();
                    loadStats();
                  } else {
                    const error = await response.json();
                    alert(error.error || 'Failed to add salary record');
                  }
                } catch (error) {
                  console.error('Error adding salary:', error);
                  alert('Failed to add salary record');
                }
              }} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employee ID *</label>
                    <input
                      type="text"
                      value={salaryForm.employeeId}
                      onChange={(e) => setSalaryForm({...salaryForm, employeeId: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      placeholder="e.g., T001 or S001"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employee Type *</label>
                    <select
                      value={salaryForm.employeeType}
                      onChange={(e) => setSalaryForm({...salaryForm, employeeType: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="teacher">Teacher</option>
                      <option value="staff">Staff</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Month *</label>
                    <input
                      type="month"
                      value={salaryForm.month}
                      onChange={(e) => setSalaryForm({...salaryForm, month: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Base Salary *</label>
                    <input
                      type="number"
                      value={salaryForm.baseSalary}
                      onChange={(e) => setSalaryForm({...salaryForm, baseSalary: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Allowances</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Housing</label>
                      <input
                        type="number"
                        value={salaryForm.allowances.housing}
                        onChange={(e) => setSalaryForm({
                          ...salaryForm,
                          allowances: {...salaryForm.allowances, housing: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Transport</label>
                      <input
                        type="number"
                        value={salaryForm.allowances.transport}
                        onChange={(e) => setSalaryForm({
                          ...salaryForm,
                          allowances: {...salaryForm.allowances, transport: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Medical</label>
                      <input
                        type="number"
                        value={salaryForm.allowances.medical}
                        onChange={(e) => setSalaryForm({
                          ...salaryForm,
                          allowances: {...salaryForm.allowances, medical: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Deductions</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tax</label>
                      <input
                        type="number"
                        value={salaryForm.deductions.tax}
                        onChange={(e) => setSalaryForm({
                          ...salaryForm,
                          deductions: {...salaryForm.deductions, tax: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Insurance</label>
                      <input
                        type="number"
                        value={salaryForm.deductions.insurance}
                        onChange={(e) => setSalaryForm({
                          ...salaryForm,
                          deductions: {...salaryForm.deductions, insurance: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
                    <select
                      value={salaryForm.paymentStatus}
                      onChange={(e) => setSalaryForm({...salaryForm, paymentStatus: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="processing">Processing</option>
                      <option value="overdue">Overdue</option>
                      <option value="issue">Issue</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                    <select
                      value={salaryForm.paymentMethod}
                      onChange={(e) => setSalaryForm({...salaryForm, paymentMethod: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="flutterwave">Flutterwave</option>
                      <option value="cash">Cash</option>
                      <option value="check">Check</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={salaryForm.notes}
                    onChange={(e) => setSalaryForm({...salaryForm, notes: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Additional notes about this salary record"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddSalaryModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Add Salary Record
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );

};

export default UnifiedSalaryManagement;



